import React, { useCallback, useState } from 'react';
import {
  useRecordContext,
  DateField,
  NumberField,
  required,
  TextField,
  TextInput,
  useNotify,
  RecordContextProvider,
} from 'react-admin';

import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';

import ModalButton from './ModalButton';
import TransactionTypeField from './TransactionTypeField';
import useCustomQuery from '../../hooks/useCustomQuery';
import useCustomRpc from '../../hooks/useCustomRpc';

const validateRequired = [required()];

const WriteOffAccountButton = () => {
  const notify = useNotify();
  const record = useRecordContext();
  const [comment, setComment] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [shouldFetch, setShouldFetch] = useState(true);

  const { data: { transactions } = {} } = useCustomQuery({
    httpMethod: 'GET',
    path: `account/${record.id}/write-off`,
    errorMessage: 'Error fetching write off transactions',
    enabled: isOpen && shouldFetch,
  });

  const { mutate: writeOff } = useCustomRpc({
    httpMethod: 'POST',
    path: `account/${record.id}/write-off`,
    errorMessage: 'Failed to write off account',
    successMessage: 'Account successfully written off',
    shouldRefresh: true,
  });

  const onClick = useCallback(() => {
    if (record.todaySolvingAmount <= 0) return false;

    if (!comment) {
      notify('Comment is mandatory', { type: 'error' });
      return false;
    }

    writeOff({ transactions, comment });
    setShouldFetch(false);
    return true;
  }, [record.todaySolvingAmount, comment, writeOff, notify, transactions]);

  return (
    <ModalButton
      onOpen={() => setIsOpen(true)}
      onClick={onClick}
      title="Write Off Account"
      loading={!!transactions}
      disabled={
        (transactions && transactions.length === 0)
        || record.todaySolvingAmount <= 0
        || !record.isWriteOffEligible
      }
    >
      <Box display="flex" flexDirection="column">
        <Typography>
          Those are the transactions that are going to be added to the account:
        </Typography>
        { transactions && (
          <List dense={false}>
            { transactions.map((transaction) => (
              <ListItem key={transaction._id} style={{ display: 'flex', justifyContent: 'flex-start', gap: '.5rem' }}>
                <RecordContextProvider value={transaction}>
                  <DateField source="date" style={{ display: 'flex', flex: 1 }} />
                  <NumberField source="amount" options={{ style: 'currency', currency: 'SEK' }} style={{ display: 'flex', flex: 1 }} />
                  <TextField source="description" style={{ display: 'flex', flex: 2 }} />
                  <Box display="flex" flex={1}>
                    <TransactionTypeField source="type" />
                  </Box>
                </RecordContextProvider>
              </ListItem>
            )) }
          </List>
        )}
        <Typography>
          Are you sure that you want to continue? Please, leave a comment below:
        </Typography>
        <TextInput
          alwaysOn
          label="Comment"
          source="comment"
          onChange={(e) => setComment(e.target.value)}
          validate={validateRequired}
        />
      </Box>
    </ModalButton>
  );
};

export default WriteOffAccountButton;
